import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FiCalendar, FiFileText } from 'react-icons/fi';
import ScreenContainer from '../components/ScreenContainer';
import CurrencyDisplay from '../components/AmountDisplay';
import { Account, CreditCardBillSummary } from '../types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAccounts } from '../store/slices/accountsSlice';
import * as apiService from '../services/apiService';

interface CreditCardBillsRouteState {
  account?: Account;
}

const parseLocalDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addMonthsToLocalDateString = (date?: string | null, months = 1) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  parsed.setMonth(parsed.getMonth() + months);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const daysUntil = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((parsed.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const formatMonthBadge = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return 'Bill';
  return parsed.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }).replace(' ', " '").toUpperCase();
};

const formatLongDate = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatStatementDate = (date?: string | null) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const CreditCardBillsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const routeState = location.state as CreditCardBillsRouteState | null;
  const fallbackAccount = routeState?.account;
  const parsedAccountId = Number(accountId);
  const resolvedAccountId = Number.isFinite(parsedAccountId) ? parsedAccountId : fallbackAccount?.id;

  const { status: accountsStatus } = useAppSelector((state) => state.accounts);
  const allAccounts = useAppSelector((state) => state.accounts.accounts);
  const storeAccount = useMemo(
    () => resolvedAccountId ? allAccounts.find((account) => account.id === resolvedAccountId) : null,
    [allAccounts, resolvedAccountId]
  );
  const account = storeAccount ?? fallbackAccount ?? null;
  const parentAccount = account?.parentAccountId
    ? allAccounts.find((candidate) => candidate.id === account.parentAccountId) ?? null
    : null;
  const billHistoryAccount = parentAccount ?? account;
  const billHistoryAccountId = billHistoryAccount?.id ?? resolvedAccountId;

  const [bills, setBills] = useState<CreditCardBillSummary[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'succeeded' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
  }, [accountsStatus, dispatch]);

  useEffect(() => {
    if (!billHistoryAccountId) {
      return;
    }

    if (account?.parentAccountId && !parentAccount) {
      return;
    }

    setStatus('loading');
    setError(null);
    apiService.fetchCreditCardBills(billHistoryAccountId)
      .then((response) => {
        setBills(response);
        setStatus('succeeded');
      })
      .catch((fetchError: any) => {
        setError(fetchError?.message || 'Failed to load credit card bills.');
        setStatus('failed');
      });
  }, [account?.parentAccountId, billHistoryAccountId, parentAccount]);

  const latestBill = bills[0] ?? billHistoryAccount?.creditCardBill ?? null;
  const nextStatementDate = useMemo(
    () => addMonthsToLocalDateString(latestBill?.statementDate),
    [latestBill?.statementDate]
  );
  const nextStatementDays = daysUntil(nextStatementDate);
  const timelineBills = bills.length > 0 ? bills : (billHistoryAccount?.creditCardBill ? [billHistoryAccount.creditCardBill] : []);

  return (
    <ScreenContainer title="Bill History">
      {status === 'loading' ? (
        <div className="flex h-full items-center justify-center px-4 text-sm text-muted-foreground">
          Loading bills...
        </div>
      ) : (
      <div className="mx-auto flex w-full max-w-sm flex-col px-4 pb-6 pt-4">
        {status === 'failed' && (
          <div className="rounded-xl border border-error/30 bg-error/10 p-4 text-sm text-error">
            {error}
          </div>
        )}

        {status !== 'failed' && !nextStatementDate && timelineBills.length === 0 && (
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            No credit card bills found for this card.
          </div>
        )}

        <div className="relative space-y-8">
          {nextStatementDate && (
            <section className="relative pl-10">
              <div className="absolute left-0 top-0 z-10 overflow-hidden rounded-lg border-[0.7px] border-border bg-secondary text-[11px] font-bold uppercase text-foreground shadow-sm">
                <div className="h-2 bg-red-500" />
                <div className="px-2.5 py-1.5">{formatMonthBadge(nextStatementDate)}</div>
              </div>
              <div className="-ml-7 pt-14">
                <div className="rounded-xl border-[0.7px] border-border bg-secondary px-4 py-5 text-center text-muted-foreground">
                  <FiFileText className="mx-auto mb-2.5 h-4 w-4" />
                  <p className="text-sm font-medium text-foreground">
                    Statement is expected to arrive
                    {nextStatementDays != null && nextStatementDays >= 0 ? ` in ${nextStatementDays} days,` : ''}
                    {` by ${formatLongDate(nextStatementDate)}`}
                  </p>
                </div>
              </div>
            </section>
          )}

          {timelineBills.map((bill) => {
            const monthDate = bill.statementDate ?? bill.dueDate;
            return (
              <section key={bill.id} className="relative pl-10">
                <div className="absolute left-0 top-0 z-10 overflow-hidden rounded-lg border-[0.7px] border-border bg-secondary text-[11px] font-bold uppercase text-foreground shadow-sm">
                  <div className="h-2 bg-red-500" />
                  <div className="px-2.5 py-1.5">{formatMonthBadge(monthDate)}</div>
                </div>
                <div className="-ml-7 pt-14">
                  <div className="rounded-xl border-[0.7px] border-border bg-secondary p-4">
                    <div className="mb-4 flex items-center gap-2 text-foreground">
                      <FiCalendar className="h-3.5 w-3.5 shrink-0" />
                      <p className="text-sm font-medium">
                        Statement arrived
                        {formatStatementDate(bill.statementDate) ? ` on ${formatStatementDate(bill.statementDate)}.` : '.'}
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                        <CurrencyDisplay
                          amount={bill.billAmount}
                          showType={false}
                          className="text-foreground"
                          valueClassName="text-lg font-bold text-foreground"
                          symbolClassName="text-foreground"
                          smallRupeeSymbol
                        />
                        <div className="border-t border-dashed border-border" />
                        <span className="text-sm font-medium text-muted-foreground">Total due</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
      )}
    </ScreenContainer>
  );
};

export default CreditCardBillsScreen;
